import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddRevisionQuestion } from '@/features/exam-revision/hooks/useAddRevisionQuestion';
import { useDeleteRevisionQuestion } from '@/features/exam-revision/hooks/useDeleteRevisionQuestion';
import { useRevisionReview } from '@/features/exam-revision/hooks/useRevisionReview';
import type { RevisionExamDetail } from '@/features/exam-revision/types/revision';
import { ConfirmDialog } from '@/features/exam-revision/ui/organisms/ConfirmDialog';
import { RevisionReviewTemplate } from '@/features/exam-revision/ui/templates/RevisionReviewTemplate';

export const Route = createFileRoute('/_authenticated/exam/revision/$examId/review/')({
  component: RevisionReviewPage,
});

export function RevisionReviewPage() {
  const { examId } = Route.useParams();
  const botExamId = Number(examId);
  const navigate = useNavigate();
  const { t } = useTranslation('examRevision');

  const {
    questions,
    current,
    currentIndex,
    total,
    typeFilter,
    availableFilters,
    setTypeFilter,
    goTo,
    updateQuestion,
    saveCurrentQuestion,
    submitReview,
    title,
    botExamLangId,
    isSaving,
    isSubmitting,
    isLoading,
  } = useRevisionReview(botExamId);

  const { deleteQuestion, isLoading: isDeleting } = useDeleteRevisionQuestion(botExamId);
  const { addQuestion, isLoading: isAdding } = useAddRevisionQuestion(botExamId);

  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const exam: RevisionExamDetail = {
    id: botExamId,
    title: title ?? '',
    disciplineName: '',
    date: '',
  };

  const handleFinalize = () => {
    submitReview({ onSuccess: () => void navigate({ to: '/exam/revision' as never }) });
  };

  const handleSave = () => {
    // Salva a questão atual sem navegar; o operador avança manualmente.
    saveCurrentQuestion();
  };

  const handleConfirmDelete = () => {
    if (deleteTargetId === null) return;
    deleteQuestion(deleteTargetId, { onSuccess: () => setDeleteTargetId(null) });
  };

  const handleAddQuestion = () => {
    if (botExamLangId === null) return;
    // A nova questão é criada vazia e anexada ao fim da lista. Após o refetch,
    // limpa o filtro e navega até a última questão (índice fica clampado à
    // recém-criada) para o operador preenchê-la de imediato.
    addQuestion(botExamLangId, {
      onSuccess: () => {
        setTypeFilter('all');
        goTo(Number.MAX_SAFE_INTEGER);
      },
    });
  };

  return (
    <>
      <RevisionReviewTemplate
        exam={exam}
        questions={questions}
        current={current}
        currentIndex={currentIndex}
        total={total}
        typeFilter={typeFilter}
        availableFilters={availableFilters}
        onTypeFilter={setTypeFilter}
        onNavigate={goTo}
        onUpdateQuestion={updateQuestion}
        onDeleteQuestion={setDeleteTargetId}
        onAddQuestion={handleAddQuestion}
        onSave={handleSave}
        onFinalize={handleFinalize}
        isSaving={isSaving}
        isFinalizing={isSubmitting}
        isLoading={isLoading || isAdding}
      />

      <ConfirmDialog
        open={deleteTargetId !== null}
        title={t('review.deleteModal.title')}
        body={t('review.deleteModal.body')}
        confirmLabel={t('review.deleteModal.confirm')}
        cancelLabel={t('review.deleteModal.cancel')}
        tone="danger"
        isLoading={isDeleting}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
